import React, { useState, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import SidePanel from '@/components/ui/SidePanel'
import { designTokens } from '@/utils/design-tokens'
import { ArticleService } from '@/api/generated/services/ArticleService'
import { ArticleUpdateRequest } from '@/api/generated/models/ArticleUpdateRequest'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import type { ArticleResponse } from '@/api/generated/models/ArticleResponse'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'

interface MeetingEditPanelProps {
  isOpen: boolean
  onClose: () => void
  selectedMeeting: ArticleResponse | null
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const MeetingEditPanel: React.FC<MeetingEditPanelProps> = ({
  isOpen,
  onClose,
  selectedMeeting,
  onSuccess
}) => {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [assigneeName, setAssigneeName] = useState('')
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [orgMembers, setOrgMembers] = useState<OrgMemberResponse[]>([])
  const [isLoadingMembers, setIsLoadingMembers] = useState(false)

  const { showError, showSuccess } = useAlert()

  // Get Redux state
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Initialize form data when selected meeting changes
  useEffect(() => {
    if (isOpen && selectedMeeting) {
      setTitle(selectedMeeting.title || '')
      setContent(selectedMeeting.content || '')
      setAssigneeId(selectedMeeting.assignee_id || '')
      setAssigneeName(selectedMeeting.assignee_name || '')
    }
  }, [isOpen, selectedMeeting])

  // Load org members when panel opens
  useEffect(() => {
    if (isOpen && selectedWorkspace?.org_id) {
      loadOrgMembers()
    }
  }, [isOpen, selectedWorkspace?.org_id])

  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('')
      setContent('')
      setAssigneeId('')
      setAssigneeName('')
      setIsAssigneeDropdownOpen(false)
    }
  }, [isOpen])

  const loadOrgMembers = async () => {
    if (!selectedWorkspace?.org_id) return

    setIsLoadingMembers(true)
    try {
      const response = await OrgMemberService.appsOrganizationViewsListOrgMembers({
        orgId: selectedWorkspace.org_id,
        workspaceId: selectedWorkspace.id,
        page: 1,
        perPage: 1000
      })

      if (response.data) {
        setOrgMembers(response.data as OrgMemberResponse[])
      }
    } catch (error) {
      console.error('Failed to load org members:', error)
      showError('담당자 목록을 불러오는 중 오류가 발생했습니다.')
    } finally {
      setIsLoadingMembers(false)
    }
  }

  const handleAssigneeSelect = (memberId: string, memberName: string) => {
    setAssigneeId(memberId)
    setAssigneeName(memberName)
    setIsAssigneeDropdownOpen(false)
  }

  const handleCancel = () => {
    onClose()
  }

  const handleSave = async () => {
    if (!selectedMeeting) {
      showError('수정할 회의록을 찾을 수 없습니다.')
      return
    }

    // Validation
    if (!title.trim()) {
      showError('제목을 입력해주세요.')
      return
    }

    if (!content.trim()) {
      showError('내용을 입력해주세요.')
      return
    }

    if (!assigneeId) {
      showError('담당자를 선택해주세요.')
      return
    }

    setIsSubmitting(true)

    try {
      const updateRequest: ArticleUpdateRequest = {
        title: title.trim(),
        content: content.trim(),
        assignee_id: assigneeId
      }

      await ArticleService.appsArticleViewsUpdateArticle({
        articleId: selectedMeeting.id,
        requestBody: updateRequest
      })

      showSuccess('회의록이 성공적으로 수정되었습니다.')
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Failed to update meeting note:', error)
      showError('회의록 수정 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formValid = title.trim() && content.trim() && assigneeId

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !formValid}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  if (!selectedMeeting) return null

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="회의록 수정"
      width={800}
      headerActions={headerActions}
    >
      <div className="px-6 py-6 space-y-8 overflow-y-auto flex-1">
        {/* 기본 정보 섹션 */}
        <div className="space-y-6">
          <SectionHeader title="기본 정보" />
          <div className="space-y-4">
            {/* 워크스페이스 정보 */}
            <InfoField
              label="워크스페이스"
              value={selectedWorkspace?.name || '-'}
            />

            {/* 생성자 정보 */}
            <InfoField
              label="생성자"
              value={selectedMeeting.creator_name || '-'}
            />

            {/* 담당자 선택 */}
            <InfoField
              label="담당자"
              value={
                <div className="relative w-full">
                  <div
                    className="flex items-center gap-1.5 px-3 py-2 h-10 border rounded cursor-pointer transition-all duration-150"
                    style={{
                      backgroundColor: designTokens.colors.white,
                      borderColor: designTokens.colors.gray[1]
                    }}
                    onClick={() => setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen)}
                  >
                    <span
                      className="flex-1 text-left"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        color: assigneeName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
                      }}
                    >
                      {assigneeName || '담당자를 선택해주세요'}
                    </span>
                    <ChevronDown
                      className="w-5 h-5"
                      style={{ color: designTokens.colors.gray[3] }}
                    />
                  </div>

                  {/* Dropdown Menu */}
                  {isAssigneeDropdownOpen && (
                    <div
                      className="absolute top-full left-0 right-0 mt-1 border rounded shadow-lg z-10 max-h-60 overflow-y-auto"
                      style={{
                        backgroundColor: designTokens.colors.white,
                        borderColor: designTokens.colors.gray[1]
                      }}
                    >
                      {isLoadingMembers ? (
                        <div className="px-3 py-2 text-center">
                          <span style={{ color: designTokens.colors.gray[3] }}>
                            로딩 중...
                          </span>
                        </div>
                      ) : orgMembers.length === 0 ? (
                        <div className="px-3 py-2 text-center">
                          <span style={{ color: designTokens.colors.gray[3] }}>
                            담당자가 없습니다
                          </span>
                        </div>
                      ) : (
                        orgMembers.map(member => (
                          <div
                            key={member.id}
                            className="px-3 py-2 cursor-pointer hover:opacity-80 transition-opacity"
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '16px',
                              color: designTokens.colors.gray[6]
                            }}
                            onClick={() => handleAssigneeSelect(member.id, member.account_name)}
                          >
                            {member.account_name}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              }
              fullWidth
            />
          </div>
        </div>

        {/* 회의록 내용 섹션 */}
        <div className="space-y-6">
          <SectionHeader title="회의록 내용" />
          <div className="space-y-4">
            {/* 제목 입력 */}
            <InfoField
              label="제목"
              value={
                <Input
                  placeholder="회의록 제목을 입력해주세요"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  size="small"
                  className="w-full"
                />
              }
              fullWidth
            />

            {/* 내용 입력 */}
            <InfoField
              label="내용"
              value={
                <textarea
                  placeholder="회의록 내용을 입력해주세요"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border rounded resize-none outline-none"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    borderColor: designTokens.colors.gray[1],
                    color: designTokens.colors.gray[6]
                  }}
                />
              }
              fullWidth
            />
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default MeetingEditPanel